import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit,
} from '@angular/core';
import { Auth } from '@freelancer/auth';
import { Datastore, DatastoreDocument } from '@freelancer/datastore';
import {
  UserBidLimitCollection,
  UserInfo,
  UserInfoCollection,
} from '@freelancer/datastore/collections';
import { Feature } from '@freelancer/feature-flags';
import { DurationFormat } from '@freelancer/pipes';
import { Badge, BadgeSize, BadgeType } from '@freelancer/ui/badge';
import { ButtonColor } from '@freelancer/ui/button';
import { LinkColor, LinkUnderline, LinkWeight } from '@freelancer/ui/link';
import { Margin } from '@freelancer/ui/margin';
import { FontColor, FontType, FontWeight, TextSize } from '@freelancer/ui/text';
import { TooltipPosition } from '@freelancer/ui/tooltip';
import * as Rx from 'rxjs';
import { map, take } from 'rxjs/operators';

@Component({
  selector: 'app-insights-bid-summary-widget',
  template: `
    <fl-card
      *ngIf="!(userBidLimitDoc.status$ | flAsync)?.error"
      [flMarginBottom]="Margin.MID"
    >
      <fl-card-header-title i18n="Insights bids widget header">
        <fl-text [size]="headerTextSize" [weight]="FontWeight.BOLD">
          Your Bids
        </fl-text>
      </fl-card-header-title>
      <fl-card-header-right *flFeature="Feature.MEMBERSHIPS">
        <ng-container *ngIf="userInfo$ | flAsync as userInfo">
          <fl-tooltip
            *ngIf="userInfo.membershipBadge; else showMembershipText"
            [message]="userInfo.membership | titlecase"
            [position]="TooltipPosition.TOP_CENTER"
          >
            <fl-link
              flTrackingLabel="InsightsBidsWidgetMembershipPageLink"
              [link]="'/membership'"
            >
              <fl-badge
                [badge]="membershipBadge$ | flAsync"
                [size]="BadgeSize.MID"
              ></fl-badge>
            </fl-link>
          </fl-tooltip>
          <ng-template #showMembershipText>
            <fl-link
              flTrackingLabel="InsightsBidsWidgetMembershipPageLink"
              [color]="LinkColor.DARK"
              [link]="'/membership'"
              [size]="TextSize.XXSMALL"
              [underline]="LinkUnderline.NEVER"
              [weight]="LinkWeight.BOLD"
            >
              {{ userInfo.membership | uppercase }}
            </fl-link>
          </ng-template>
        </ng-container>
      </fl-card-header-right>
      <ng-container
        *ngIf="(userBidLimitDoc.status$ | flAsync)?.ready; else loadingState"
      >
        <fl-bit
          *ngIf="userBidLimitDoc.valueChanges() | flAsync as userBidLimit"
        >
          <fl-bit
            [flMarginBottom]="
              (showBidReplenishment$ | flAsync) ? Margin.XSMALL : Margin.NONE
            "
          >
            <fl-text
              [fontType]="FontType.SPAN"
              [size]="bidCountTextSize"
              [weight]="FontWeight.BOLD"
            >
              {{ userBidLimit.bidsRemaining }}
              <ng-container
                *ngIf="userBidLimit.bidsRemaining !== 1; else bidsSingular"
                i18n="Insights bid widget bid label plural"
              >
                bids
              </ng-container>
              <ng-template #bidsSingular>
                <ng-container i18n="Insights bid widget bid label singular">
                  bid
                </ng-container>
              </ng-template>
            </fl-text>
            <fl-text
              i18n="Insights bid widget total bids label"
              [fontType]="FontType.SPAN"
              [size]="bidCountTextSize"
            >
              left out of {{ userBidLimit.bidLimit }}
            </fl-text>
          </fl-bit>
          <fl-bit
            *ngIf="showBidReplenishment$ | flAsync"
            class="ReplenishmentData"
          >
            <fl-icon
              [flMarginRight]="Margin.XXXSMALL"
              [name]="'ui-clock-alt-v2'"
            ></fl-icon>
            <fl-bit>
              <fl-relative-time
                [color]="FontColor.DARK"
                [date]="userBidLimit.nextBidRefreshTime"
                [strict]="true"
                [secondaryUnit]="true"
                [size]="bidReplenishmentTextSize"
                [weight]="FontWeight.BOLD"
                (timePassed)="loadUserBidData()"
              ></fl-relative-time>
              <fl-text
                i18n="Insights bid widget replenishment rate label"
                [fontType]="FontType.SPAN"
                [size]="bidReplenishmentTextSize"
              >
                until next additional bid ({{ userBidLimit.bidRefreshRate }}x
                replenishment rate)
              </fl-text>
            </fl-bit>
          </fl-bit>
        </fl-bit>
      </ng-container>
      <ng-template #loadingState>
        <fl-loading-text
          [flMarginBottom]="Margin.XSMALL"
          [rows]="1"
        ></fl-loading-text>
        <fl-loading-text
          [flMarginBottom]="Margin.MID"
          [rows]="2"
        ></fl-loading-text>
      </ng-template>
      <fl-button
        *flFeature="Feature.INSIGHTS"
        class="InsightsBidsLinkButton"
        flTrackingLabel="InsightsBidsWidgetInsightsPageLink"
        i18n="Insights bid widget button link"
        [color]="ButtonColor.TRANSPARENT_DARK"
        [link]="'/insights/bids'"
      >
        View Bid Insights
      </fl-button>
    </fl-card>
  `,
  styleUrls: ['./insights-bid-summary-widget.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InsightsBidSummaryWidgetComponent implements OnInit {
  BadgeSize = BadgeSize;
  ButtonColor = ButtonColor;
  DurationFormat = DurationFormat;
  Feature = Feature;
  FontColor = FontColor;
  FontType = FontType;
  FontWeight = FontWeight;
  LinkColor = LinkColor;
  LinkUnderline = LinkUnderline;
  LinkWeight = LinkWeight;
  Margin = Margin;
  TextSize = TextSize;
  TooltipPosition = TooltipPosition;

  membershipBadge$: Rx.Observable<Badge | undefined>;
  private showBidReplenishmentSubject$ = new Rx.BehaviorSubject<boolean>(false);
  showBidReplenishment$ = this.showBidReplenishmentSubject$.asObservable();
  userBidLimitDoc: DatastoreDocument<UserBidLimitCollection>;
  userInfo$: Rx.Observable<UserInfo>;

  @Input() bidCountTextSize = TextSize.SMALL;
  @Input() bidReplenishmentTextSize = TextSize.XSMALL;
  @Input() headerTextSize = TextSize.INHERIT;

  constructor(private auth: Auth, private datastore: Datastore) {}

  ngOnInit() {
    this.userInfo$ = this.datastore
      .document<UserInfoCollection>('userInfo', this.auth.getUserId())
      .valueChanges();

    this.membershipBadge$ = this.userInfo$.pipe(
      map(userInfo =>
        userInfo.membershipBadge
          ? {
              type: BadgeType.MEMBERSHIP,
              badge: userInfo.membershipBadge,
            }
          : undefined,
      ),
    );

    this.loadUserBidData();
  }

  loadUserBidData() {
    this.userBidLimitDoc = this.datastore.document<UserBidLimitCollection>(
      'userBidLimit',
      this.auth.getUserId(),
    );

    this.userBidLimitDoc
      .valueChanges()
      .pipe(take(1))
      .toPromise()
      .then(bidLimit => {
        this.showBidReplenishmentSubject$.next(
          bidLimit.nextBidRefreshTime > new Date().getTime() &&
            bidLimit.bidsRemaining < bidLimit.bidLimit,
        );
      });
  }
}
